import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import "./BotonRojoConafModal.css";

/* ======================
   Helpers formato
====================== */
const pad2 = (n) => String(n).padStart(2, "0");

const formatFechaChile = (ymd) => {
  if (!ymd || typeof ymd !== "string") return "—";
  const [y, m, d] = ymd.split("-");
  if (!y || !m || !d) return ymd;
  return `${pad2(d)}-${pad2(m)}-${y}`;
};

const formatNumberCL = (n) => {
  if (n === null || n === undefined || n === "") return "—";
  const num = Number(n);
  if (!Number.isFinite(num)) return "—";
  return num.toLocaleString("es-CL");
};

const formatPercent = (p) => {
  if (p === null || p === undefined || p === "") return "—";
  const num = Number(p);
  if (!Number.isFinite(num)) return "—";
  // tus datos vienen 0..1
  return `${Math.round(num * 100)}%`;
};

const formatFetchedAt = (ts) => {
  if (!ts) return "—";
  try {
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString("es-CL", { timeZone: "America/Santiago" });
  } catch {
    return "—";
  }
};

const normalizeStr = (s) =>
  String(s || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

/* ======================
   Detector robusto
   - Encuentra un array (o map numérico) que "parece" lista de comunas
====================== */
function isPlainObject(v) {
  return v && typeof v === "object" && !Array.isArray(v);
}

function looksLikeComunaRow(x) {
  if (!x || typeof x !== "object") return false;
  // mínimo: com + (prov o reg) + date
  const hasCom = typeof x.com === "string" && x.com.trim().length > 0;
  const hasProv = typeof x.prov === "string" && x.prov.trim().length > 0;
  const hasReg = typeof x.reg === "string" && x.reg.trim().length > 0;
  const hasDate = typeof x.date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(x.date);
  // tolerante: a veces date puede venir distinto, pero en tu captura es date.
  return hasCom && (hasProv || hasReg) && hasDate;
}

function isNumericKeyedMap(obj) {
  if (!isPlainObject(obj)) return false;
  const keys = Object.keys(obj);
  if (keys.length < 1) return false;
  // Si casi todas las llaves son números ("0","1","2"...), lo tratamos como lista
  let numeric = 0;
  for (const k of keys) if (/^\d+$/.test(k)) numeric++;
  return numeric >= Math.max(1, Math.floor(keys.length * 0.8));
}

function findBestList(root) {
  // retorna { list: [], path: "..." }
  const visited = new Set();
  let best = { list: [], path: "no-detectado", score: 0 };

  const scoreList = (arr) => {
    if (!Array.isArray(arr) || arr.length === 0) return 0;
    let ok = 0;
    // muestreamos hasta 30
    const n = Math.min(arr.length, 30);
    for (let i = 0; i < n; i++) if (looksLikeComunaRow(arr[i])) ok++;
    // score pondera cantidad total y % de filas válidas
    const ratio = ok / n;
    return ok * 10 + ratio * 100 + Math.min(arr.length, 200) * 0.1;
  };

  const toArrayIfMap = (v) => {
    if (Array.isArray(v)) return v;
    if (isNumericKeyedMap(v)) return Object.values(v);
    return null;
  };

  const walk = (node, path, depth) => {
    if (!node || depth > 6) return;
    if (visited.has(node)) return;
    if (typeof node === "object") visited.add(node);

    // 1) si es array o map numérico, lo evaluamos
    const arr = toArrayIfMap(node);
    if (arr) {
      const sc = scoreList(arr);
      if (sc > best.score) best = { list: arr, path, score: sc };
    }

    // 2) si es objeto, recorremos props
    if (isPlainObject(node)) {
      for (const [k, v] of Object.entries(node)) {
        // evita recorrer campos gigantes no relevantes a infinito
        walk(v, path ? `${path}.${k}` : k, depth + 1);
      }
    }

    // 3) si es array, recorremos algunos elementos (por si hay wrappers)
    if (Array.isArray(node)) {
      const n = Math.min(node.length, 15);
      for (let i = 0; i < n; i++) {
        walk(node[i], `${path}[${i}]`, depth + 1);
      }
    }
  };

  walk(root, "", 0);
  return { list: best.list, path: best.path || "no-detectado" };
}

/* ======================
   COMPONENTE
====================== */
export default function BotonRojoConafModal({ open, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [selectedComuna, setSelectedComuna] = useState("ALL");

  // Cerrar con ESC + bloquear scroll
  useEffect(() => {
    if (!open) return;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;

    setLoading(true);
    const ref = doc(db, "external_cache", "botonRojoConaf");

    const unsub = onSnapshot(
      ref,
      (snap) => {
        setData(snap.exists() ? snap.data() : null);
        setLoading(false);
      },
      (err) => {
        console.error("BotonRojoConafModal onSnapshot:", err);
        setData(null);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [open]);

  /* ======================
     1) Detectar lista real
====================== */
  const detected = useMemo(() => {
    if (!data) return { list: [], path: "sin-data" };

    // A) Primero intentos directos (por si los tienes)
    const directCandidates = [
      ["dias", data?.dias],
      ["comunas", data?.comunas],
      ["items", data?.items],
      ["rows", data?.rows],
      ["list", data?.list],
      ["data", data?.data],
      ["payload", data?.payload],
      ["result", data?.result],
    ];

    for (const [name, val] of directCandidates) {
      if (Array.isArray(val) && val.some(looksLikeComunaRow)) {
        return { list: val, path: name };
      }
      if (isNumericKeyedMap(val)) {
        const arr = Object.values(val);
        if (arr.some(looksLikeComunaRow)) return { list: arr, path: `${name}(map)` };
      }
    }

    // B) Si no, buscamos recursivo dentro de todo el documento
    return findBestList(data);
  }, [data]);

  const items = useMemo(() => {
    // Normalizamos para que SIEMPRE tenga las llaves que tu UI usa
    const src = Array.isArray(detected.list) ? detected.list : [];
    return src
      .filter((x) => x && typeof x === "object")
      .map((c) => ({
        reg: c?.reg ?? "",
        prov: c?.prov ?? "",
        com: c?.com ?? "",
        date: c?.date ?? null,
        horas: c?.horas ?? null,
        SUM_br_ha: c?.SUM_br_ha ?? c?.sum_br_ha ?? c?.SUM_BR_HA ?? null,
        proportion: c?.proportion ?? null,
      }))
      .filter((x) => x.com); // asegura comuna
  }, [detected]);

  /* ======================
     2) Consolidar comunas
====================== */
  const consolidated = useMemo(() => {
    const map = new Map();

    for (const it of items) {
      const reg = String(it?.reg || "").trim();
      const prov = String(it?.prov || "").trim();
      const com = String(it?.com || "").trim();
      const date = String(it?.date || "").trim();

      if (!com) continue;

      const key = `${normalizeStr(reg)}|${normalizeStr(prov)}|${normalizeStr(com)}`;
      const ha = Number(it?.SUM_br_ha);
      const prop = Number(it?.proportion);

      if (!map.has(key)) {
        map.set(key, {
          reg,
          prov,
          com,
          minDate: date || "",
          maxDate: date || "",
          datesSet: new Set(date ? [date] : []),
          haTotal: Number.isFinite(ha) ? ha : 0,
          propMax: Number.isFinite(prop) ? prop : null,
        });
      } else {
        const agg = map.get(key);

        if (date) {
          agg.datesSet.add(date);
          if (!agg.minDate || date < agg.minDate) agg.minDate = date;
          if (!agg.maxDate || date > agg.maxDate) agg.maxDate = date;
        }

        if (Number.isFinite(ha)) agg.haTotal += ha;

        if (Number.isFinite(prop)) {
          if (agg.propMax === null || prop > agg.propMax) agg.propMax = prop;
        }
      }
    }

    return Array.from(map.values()).map((x) => ({
      ...x,
      diasActivos: x.datesSet.size || 0,
    }));
  }, [items]);

  const comunasOptions = useMemo(() => {
    const set = new Set();
    for (const it of consolidated) {
      const name = String(it?.com || "").trim();
      if (name) set.add(name);
    }
    return ["ALL", ...Array.from(set).sort((a, b) => a.localeCompare(b, "es"))];
  }, [consolidated]);

  const filtered = useMemo(() => {
    const s = normalizeStr(search);

    const byDropdown =
      selectedComuna === "ALL"
        ? consolidated
        : consolidated.filter((x) => String(x?.com || "").trim() === selectedComuna);

    if (!s) return byDropdown;
    return byDropdown.filter((x) => normalizeStr(x?.com).includes(s));
  }, [consolidated, selectedComuna, search]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    // Orden: haTotal desc, luego comuna
    arr.sort((a, b) => {
      const haA = Number(a?.haTotal) || 0;
      const haB = Number(b?.haTotal) || 0;
      if (haB !== haA) return haB - haA;
      return String(a?.com || "").localeCompare(String(b?.com || ""), "es");
    });
    return arr;
  }, [filtered]);

  const totalGlobal = data?.totalComunasActivas ?? consolidated.length ?? 0;
  const totalMostrado = sorted.length;

  if (!open) return null;

  const modalUI = (
    <div className="br-overlay" onClick={onClose} role="dialog" aria-modal="true">
      <div className="br-modal" onClick={(e) => e.stopPropagation()}>
        <div className="br-header">
          <div className="br-title">
            <span className="br-dot" />
            <div>
              <div className="br-h1">Botón Rojo CONAF — Comunas activas</div>
              <div className="br-sub">
                Total (Chile): <b>{totalGlobal}</b> • Mostrando:{" "}
                <b>{totalMostrado}</b> • Actualizado:{" "}
                <b>{formatFetchedAt(data?.fetchedAt)}</b>
              </div>

              {data?.rango?.desde && data?.rango?.hasta ? (
                <div className="br-sub2">
                  Ventana pronóstico: <b>{formatFechaChile(data.rango.desde)}</b>{" "}
                  → <b>{formatFechaChile(data.rango.hasta)}</b>
                </div>
              ) : null}

              {/* ✅ Debug visible (sin consola) */}
              <div className="br-sub2" style={{ marginTop: 6, opacity: 0.9 }}>
                Detectados: <b>{items.length}</b> • Fuente: <b>{detected.path || "—"}</b>
              </div>
            </div>
          </div>

          <button className="br-close" onClick={onClose}>
            Cerrar
          </button>
        </div>

        <div className="br-filters">
          <div className="br-filter">
            <label>Filtrar por comuna</label>
            <select
              value={selectedComuna}
              onChange={(e) => {
                setSelectedComuna(e.target.value);
                setSearch("");
              }}
            >
              {comunasOptions.map((c) => (
                <option key={c} value={c}>
                  {c === "ALL" ? "Todas las comunas" : c}
                </option>
              ))}
            </select>
          </div>

          <div className="br-filter">
            <label>Buscar comuna</label>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Ej: Paine, Copiapó…"
            />
          </div>

          <button
            className="br-btn-clear"
            onClick={() => {
              setSelectedComuna("ALL");
              setSearch("");
            }}
          >
            Limpiar
          </button>
        </div>

        <div className="br-body">
          {loading ? (
            <div className="br-empty">Cargando…</div>
          ) : !data ? (
            <div className="br-empty">Aún no hay información disponible.</div>
          ) : sorted.length === 0 ? (
            <div className="br-empty">
              No hay comunas activas para los filtros seleccionados.
            </div>
          ) : (
            <div className="br-grid">
              {sorted.map((c, idx) => (
                <div
                  className="br-card"
                  key={`${c.com}-${c.prov}-${c.reg}-${c.maxDate}-${idx}`}
                >
                  <div className="br-card-top">
                    <div className="br-comuna">{c.com || "—"}</div>
                    <div className="br-fecha">
                      {c.minDate && c.maxDate && c.minDate !== c.maxDate ? (
                        <>
                          Activa: <b>{formatFechaChile(c.minDate)}</b> →{" "}
                          <b>{formatFechaChile(c.maxDate)}</b>
                        </>
                      ) : (
                        <>
                          Pronóstico para: <b>{formatFechaChile(c.maxDate || c.minDate)}</b>
                        </>
                      )}
                      <div style={{ marginTop: 4, opacity: 0.9 }}>
                        Días activos: <b>{c.diasActivos}</b>
                      </div>
                    </div>
                  </div>

                  <div className="br-meta">
                    <div>
                      Provincia: <b>{c.prov || "—"}</b>
                    </div>
                    <div>
                      Región: <b>{c.reg || "—"}</b>
                    </div>
                  </div>

                  <div className="br-row">
                    <div className="br-pill">
                      <span>Ha en BR (total ventana)</span>
                      <b>{formatNumberCL(c.haTotal)}</b>
                    </div>

                    <div className="br-pill">
                      <span>Proporción (máx ventana)</span>
                      <b>{formatPercent(c.propMax)}</b>
                    </div>
                  </div>

                  <div className="br-footer">Fuente: CONAF (ArcGIS)</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modalUI, document.body);
}
